/**
 * useVersionCheck hook
 *
 * Checks client-API version compatibility on app startup.
 * Calls GET /api/v1/health/version?clientVersion=X&platform=Y
 * and surfaces warnings/blocks when versions are incompatible.
 */

import { useEffect, useState } from "react";

export interface VersionCheckResult {
  status: "loading" | "compatible" | "incompatible" | "error";
  apiVersion?: string;
  clientVersion: string;
  message?: string;
}

interface VersionCheckOptions {
  apiBaseUrl: string;
  clientVersion: string;
  platform: "web" | "desktop" | "admin";
  /** If true, don't block app usage on incompatibility — just warn */
  warnOnly?: boolean;
}

export function normalizeApiBaseUrl(rawUrl: string): string {
  const normalized = rawUrl.replace(/\/+$/, "");

  if (/\/api\/v1\/admin$/i.test(normalized)) {
    return normalized.replace(/\/api\/v1\/admin$/i, "");
  }

  if (/\/api\/admin$/i.test(normalized)) {
    return normalized.replace(/\/api\/admin$/i, "");
  }

  if (/\/api\/v1$/i.test(normalized)) {
    return normalized.replace(/\/api\/v1$/i, "");
  }

  if (/\/api$/i.test(normalized)) {
    return normalized.replace(/\/api$/i, "");
  }

  return normalized;
}

export function useVersionCheck(
  options: VersionCheckOptions,
): VersionCheckResult {
  const { apiBaseUrl, clientVersion, platform } = options;
  const [result, setResult] = useState<VersionCheckResult>({
    status: "loading",
    clientVersion,
  });

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const baseUrl = normalizeApiBaseUrl(apiBaseUrl);
        const url = `${baseUrl}/api/v1/health/version?clientVersion=${encodeURIComponent(clientVersion)}&platform=${platform}`;

        const resp = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: AbortSignal.timeout(5000),
        });

        if (!resp.ok) {
          if (!cancelled) {
            setResult({
              status: "error",
              clientVersion,
              message: `Version check failed (HTTP ${resp.status})`,
            });
          }
          return;
        }

        const data = await resp.json();

        if (!cancelled) {
          if (data.compatibility?.compatible === false) {
            setResult({
              status: "incompatible",
              apiVersion: data.api?.version,
              clientVersion,
              message: data.compatibility.message,
            });
          } else {
            setResult({
              status: "compatible",
              apiVersion: data.api?.version,
              clientVersion,
            });
          }
        }
      } catch {
        // Network error — don't block offline-capable apps
        if (!cancelled) {
          setResult({
            status: "error",
            clientVersion,
            message: "Could not reach server for version check",
          });
        }
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, clientVersion, platform]);

  return result;
}
